import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../lib/format";
import { useTranslation } from "react-i18next";
import { adminApi, type Invitation, type User, type UserStatus } from "../../api/admin";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Pagination } from "../../components/Pagination";
import { Select } from "../../components/Select";
import { InviteUserForm } from "../../components/InviteUserForm";
import { UserSalesScopePicker } from "../../components/admin/UserSalesScopePicker";
import { useAuth } from "../../auth/AuthContext";
import { ALL_ROLES } from "../../auth/roles";

const PAGE_SIZE = 25;

// English fallbacks for each role label; rendered via t("admin.users.role.<CODE>", fallback).
const ROLE_LABEL: Record<string, string> = {
  SUPERADMIN: "Super admin",
  ADMIN: "Administrator",
  SALES_DIRECTOR: "Sales director",
  SALES: "Sales",
  OTKUP: "Procurement (buy-in)",
  KVALITET: "Quality control",
  KVALITET_LEAD: "Head of quality control",
  MARKETING: "Marketing",
  CUST_PROCUREMENT: "Procurement",
  CUST_MANAGER: "Account manager",
  SUPP_MANAGER: "Supplier manager",
};

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasRole, hasPermission } = useAuth();
  const canEdit = hasPermission("section:users:write");
  const canManage = hasPermission("section:users:full");
  const canInvite = hasPermission("section:invitations:write");
  const canRevokeInvitation = hasPermission("section:invitations:full");
  const canViewInvitations = hasPermission("section:invitations:read");
  const canViewPermissions = hasPermission("section:access_control:read");
  const isCustManagerOnly = hasRole("CUST_MANAGER") && !hasRole("ADMIN") && !hasRole("SUPERADMIN") && !hasRole("SALES");
  const pageTitle = isCustManagerOnly ? t("admin.users.myTeam", "My team") : t("admin.users.title", "Users");
  const [items, setItems] = useState<User[] | null>(null);
  const [pending, setPending] = useState<Invitation[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [inviting, setInviting] = useState(false);

  async function load() {
    setError(null);
    try {
      const result = await adminApi.listUsers(page, PAGE_SIZE);
      setItems(result.content.filter(Boolean));
      setTotalPages(result.totalPages);
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  async function loadPending() {
    try {
      setPending(await adminApi.listPendingInvitations());
    } catch {
      setPending([]);
    }
  }

  useEffect(() => {
    void load();
  }, [page]);

  useEffect(() => {
    if (canViewInvitations) void loadPending();
    else setPending([]);
  }, [canViewInvitations]);

  async function handleResend(inv: Invitation) {
    try {
      await adminApi.resendInvitation(inv.id);
      toast.success(t("admin.users.toast.resent", "Invitation re-sent to {{email}}.", { email: inv.email }));
      await loadPending();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleRevoke(inv: Invitation) {
    if (!(await confirm({ message: t("admin.users.confirmRevoke", "Revoke pending invitation for {{email}}?", { email: inv.email }), danger: true }))) return;
    try {
      await adminApi.revokeInvitation(inv.id);
      toast.success(t("admin.users.toast.revoked", "Invitation revoked."));
      await loadPending();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function toggle(u: User) {
    try {
      if (u.status === "ACTIVE") await adminApi.disableUser(u.id);
      else await adminApi.enableUser(u.id);
      await load();
      toast.success(u.status === "ACTIVE" ? t("admin.users.toast.disabled", "User disabled.") : t("admin.users.toast.enabled", "User enabled."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{pageTitle}</h1>
        {canInvite && <button type="button" onClick={() => setInviting(true)} style={primary}>{t("admin.users.inviteUser", "+ Invite user")}</button>}
      </header>
      {error && <div style={errorBox}>{error}</div>}

      {pending.length > 0 && (
        <section style={panelStyle}>
          <h2 style={panelTitleStyle}>{t("admin.users.pendingTitle", "Pending invitations ({{count}})", { count: pending.length })}</h2>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t("admin.users.col.email", "Email")}</th>
                <th style={th}>{t("admin.users.col.company", "Company")}</th>
                <th style={th}>{t("admin.users.col.roles", "Roles")}</th>
                <th style={th}>{t("admin.users.col.expires", "Expires")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.users.col.actions", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.id}>
                  <td style={td}>{p.email}</td>
                  <td style={td}>
                    {p.companyName ?? <span style={{ color: "var(--ink-4)" }}>{t("admin.users.internal", "Interno (Bankom)")}</span>}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {p.roles.map((r) => (
                        <span key={r} style={chip}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td style={td}>{formatDate(p.expiresAt)}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {canInvite && <button type="button" onClick={() => handleResend(p)} style={linkBtn}>{t("admin.users.action.resend", "Resend")}</button>}
                    {canRevokeInvitation && <button type="button" onClick={() => handleRevoke(p)} style={dangerLink}>{t("admin.users.action.revoke", "Revoke")}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {!items ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.users.loading", "Loading…")}</p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.users.empty", "No users yet.")}</p>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t("admin.users.col.email", "Email")}</th>
              <th style={th}>{t("admin.users.col.name", "Name")}</th>
              <th style={th}>{t("admin.users.col.company", "Company")}</th>
              <th style={th}>{t("admin.users.col.roles", "Roles")}</th>
              <th style={th}>{t("admin.users.col.status", "Status")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("admin.users.col.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.firstName} {u.lastName}</td>
                <td style={td}>{u.companyName ?? <span style={{ color: "var(--ink-4)" }}>{t("admin.users.internal", "Interno (Bankom)")}</span>}</td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {u.roles.map((r) => (
                      <span key={r} style={chip}>{r}</span>
                    ))}
                  </div>
                </td>
                <td style={td}><StatusBadge status={u.status} /></td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  {canViewPermissions && <Link to={`/admin/permissions?tab=users&user=${encodeURIComponent(u.id)}`} style={linkBtn}>{t("access.title", "Access permissions")}</Link>}
                  {canEdit && <button type="button" onClick={() => setEditing(u)} style={linkBtn}>{t("admin.users.action.edit", "Edit")}</button>}
                  {canManage && <button type="button" onClick={() => toggle(u)} style={linkBtn}>
                    {u.status === "ACTIVE" ? t("admin.users.action.disable", "Disable") : t("admin.users.action.enable", "Enable")}
                  </button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      {editing && canEdit && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {inviting && canInvite && (
        <div onClick={() => setInviting(false)} className="modal-overlay" style={overlay}>
          <div onClick={(e) => e.stopPropagation()} className="modal-panel" style={dialog}>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{t("admin.users.inviteModalTitle", "Invite user")}</h2>
            <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
              {t("admin.users.inviteModalHint", "The recipient gets an email with a 7-day link to set their password and finish sign-up.")}
            </p>
            <InviteUserForm
              onCancel={() => setInviting(false)}
              onCreated={(inv) => {
                setInviting(false);
                toast.success(t("admin.users.toast.sent", "Invitation sent to {{email}}.", { email: inv.email }));
                void loadPending();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function UserEditModal({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const { hasPermission, hasRole } = useAuth();
  const canManageRoles = hasPermission("section:users:full");
  const canManageSystemRole = canManageRoles && hasRole("SUPERADMIN");
  const canManageScope = hasPermission("section:market_settings:full");
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [locale, setLocale] = useState(user.defaultLocale);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const [erpId, setErpId] = useState(user.erpId ?? "");
  const [erpSector, setErpSector] = useState(user.erpSector == null ? "" : String(user.erpSector));
  const [maxDiscount, setMaxDiscount] = useState(user.maxDiscountPct == null ? "" : String(user.maxDiscountPct));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const salesRole = roles.includes("SALES") || roles.includes("SALES_DIRECTOR");

  function toggleRole(r: string) {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminApi.updateUser(user.id, {
        firstName,
        lastName,
        defaultLocale: locale,
        erpId: user.internal ? (erpId.trim() || null) : undefined,
        // Zero clears the sector; left out it is not touched.
        erpSector: user.internal ? (erpSector.trim() === "" ? 0 : Number(erpSector)) : undefined,
        // Sales reps only. Empty field clears the cap (-1 sentinel); omitted for
        // non-sales users so it isn't touched.
        maxDiscountPct: salesRole ? (maxDiscount.trim() === "" ? -1 : Number(maxDiscount)) : undefined,
        ...(canManageRoles ? { roles } : {}),
      });
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={onClose} className="modal-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="modal-panel" style={dialog}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{t("admin.users.modal.editTitle", "Edit user")}</h2>
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>{user.email}</p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="form-row-wrap" style={{ display: "flex", gap: 12 }}>
            <Field label={t("admin.users.field.firstName", "First name")} style={{ flex: 1 }}>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={input} />
            </Field>
            <Field label={t("admin.users.field.lastName", "Last name")} style={{ flex: 1 }}>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={input} />
            </Field>
          </div>
          <Field label={t("admin.users.field.locale", "Locale")}>
            <Select<string>
              value={locale}
              onChange={setLocale}
              options={["sr", "en", "de", "hr", "sl", "it", "es", "fr", "ru", "tr"].map((l) => ({ value: l, label: l }))}
            />
          </Field>
          <Field label={t("admin.users.field.roles", "Roles")}>
            <div className="form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {ALL_ROLES.map((r) => (
                <label key={r} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={roles.includes(r)} disabled={!canManageRoles || (r === "SUPERADMIN" && !canManageSystemRole)} onChange={() => toggleRole(r)} />
                  {t(`admin.users.role.${r}`, ROLE_LABEL[r] ?? r)}
                </label>
              ))}
            </div>
          </Field>
          {user.internal && (
            <Field label={t("admin.users.field.erpId", "ERP ID")}>
              <input
                value={erpId}
                onChange={(e) => setErpId(e.target.value)}
                style={input}
                maxLength={64}
                placeholder={t("admin.users.field.erpIdPlaceholder", "optional")}
              />
            </Field>
          )}
          {user.internal && (
            <Field label={t("admin.users.field.erpSector", "ERP sector")}>
              <input
                type="number"
                min={1}
                step={1}
                value={erpSector}
                onChange={(e) => setErpSector(e.target.value)}
                style={input}
                placeholder={t("admin.users.field.erpIdPlaceholder", "optional")}
              />
            </Field>
          )}
          {salesRole && (
            <Field label={t("admin.users.field.maxDiscount", "Max discount %")}>
              <input
                type="number"
                min={0}
                max={100}
                step="0.5"
                value={maxDiscount}
                onChange={(e) => setMaxDiscount(e.target.value)}
                style={{ ...input, width: 120 }}
                placeholder={t("admin.users.field.maxDiscountPlaceholder", "no limit")}
              />
              <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                {t("admin.users.field.maxDiscountHelp", "Highest discount this rep can grant on orders and offers. Blank = no limit.")}
              </span>
            </Field>
          )}
          {user.internal && canManageScope && (
            <Field label={t("admin.users.field.salesScope", "Sales scope")}>
              <UserSalesScopePicker userId={user.id} />
            </Field>
          )}
          {error && <div style={errorBox}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={secondary}>{t("admin.users.action.cancel", "Cancel")}</button>
            <button type="submit" disabled={submitting} style={primary}>
              {submitting ? t("admin.users.action.saving", "Saving…") : t("admin.users.action.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  const palette: Record<UserStatus, { bg: string; fg: string }> = {
    ACTIVE: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    LOCKED: { bg: "var(--warn-bg)", fg: "var(--warn)" },
    DISABLED: { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  const c = palette[status];
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {t(`admin.users.status.${status}`, status)}
    </span>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, ...style }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  background: "var(--surface)",
  borderRadius: 8,
  overflow: "hidden",
  boxShadow: "var(--shadow-1)",
};
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const chip: React.CSSProperties = { background: "var(--tone-indigo-bg)", color: "var(--tone-indigo)", fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600 };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", marginRight: 8, fontSize: 13 };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 16,
  background: "var(--tone-orange-bg)",
  border: "1px solid var(--tone-orange)",
  borderRadius: 10,
};
const panelTitleStyle: React.CSSProperties = { margin: 0, fontSize: 14, color: "var(--tone-orange)", fontWeight: 600 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 520, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14 };
